import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Toolbar,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import Order from "../components/Order";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase-config";
import { UserContext } from "../contexts/userContext";
import { getFormattedDate } from "../utils/utils";
import { IOrder } from "../interfaces";
import {
  BUSINESS_COLLECTION,
  SALES_COLLECTION,
  // SHIFT_COLLECTION removed
} from "../utils/constants";

const KitchenOrders = () => {
  const { businessData, handleSignOut } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [completedOrders, setCompletedOrders] = useState([] as IOrder[]);
  const [otherOrders, setOtherOrders] = useState([] as IOrder[]);
  const [cancelledOrders, setCancelledOrders] = useState([] as IOrder[]);

  // removed shift subscription; kitchen orders fetched by time range below

  useEffect(() => {
    if (!businessData.uid) return;
    setLoading(true);
    const salesCollectionRef = collection(
      db,
      BUSINESS_COLLECTION,
      businessData.uid,
      SALES_COLLECTION
    );

    const now = new Date();
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      16,
      0,
      0
    );
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + 1,
      5,
      0,
      0
    );

    const q = query(
      salesCollectionRef,
      where("createdAt", ">=", start),
      where("createdAt", "<=", end),
      where("requireKitchen", "==", true),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const allOrders = snapshot.docs.map(
          (doc) =>
            ({
              ...doc.data(),
              id: doc.id,
              createdAt: getFormattedDate(doc.data().createdAt),
              updatedAt: getFormattedDate(doc.data().updatedAt),
            } as IOrder)
        );
        setCompletedOrders(
          allOrders.filter((order) => order.status === "completed")
        );
        setCancelledOrders(
          allOrders.filter((order) => order.status == "cancelled")
        );
        setOtherOrders(
          allOrders.filter(
            (order) =>
              order.status !== "completed" && order.status !== "cancelled"
          )
        );
        setLoading(false);
      },
      (error) => {
        console.error("Error al obtener pedidos en tiempo real: ", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [businessData.uid]);

  return (
    <Container sx={{ mb: 5 }}>
      <AppBar component="nav">
        <Toolbar sx={{ display: "flex", justifyContent: "end" }}>
          <Button
            size="small"
            color="secondary"
            variant="text"
            onClick={handleSignOut}
          >
            Cerrar sesión
          </Button>
        </Toolbar>
      </AppBar>
      <Grid container width="100%" display="flex" spacing={2} mt={7} p={3}>
        {loading ? (
          <Grid
            container
            width="100%"
            display="flex"
            justifyContent="center"
            mt={5}
          >
            <CircularProgress size={120} />
          </Grid>
        ) : (
          <>
            {/* Órdenes NO completadas */}
            <Grid size={12} mb={3}>
              <Typography variant="h6" color="primary" mb={1}>
                Órdenes en proceso
              </Typography>
              {otherOrders.length > 0 ? (
                <Grid container spacing={2}>
                  {otherOrders.map((order) => (
                    <Grid size={{ xs: 12, md: 6, lg: 3 }} key={order.id}>
                      <Order order={order} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="warning">No hay órdenes en proceso.</Alert>
              )}
            </Grid>
            {/* Órdenes COMPLETADAS */}
            <Grid size={12} mt={4}>
              <Typography variant="h6" color="success.main" mb={1}>
                Órdenes completadas
              </Typography>
              {completedOrders.length > 0 ? (
                <Grid container spacing={2}>
                  {completedOrders.map((order) => (
                    <Grid size={{ xs: 12, md: 4, lg: 2 }} key={order.id}>
                      <Order order={order} minimized />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Alert severity="info">No hay órdenes completadas.</Alert>
              )}
            </Grid>

            {/* Órdenes CANCELADAS */}
            {cancelledOrders.length > 0 && (
              <Grid size={12} mt={4}>
                <Typography variant="h6" color="error" mb={1}>
                  Órdenes canceladas
                </Typography>
                <Grid container spacing={2}>
                  {cancelledOrders.map((order) => (
                    <Grid size={{ xs: 12, md: 4, lg: 2 }} key={order.id}>
                      <Order order={order} minimized />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Container>
  );
};

export default KitchenOrders;
